import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { CellContext, createColumnHelper } from '@tanstack/react-table';

import InfoHeader from 'components/shared/InfoHeader';
import Tooltip from 'components/shared/Tooltip';
import { simpleDate, simpleDateAtTime } from 'lib/date';
import Workflow from 'models/Workflow';
import { Tab } from 'stores/inboundReferralsStore';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import AISparkleIcon from 'svg/AISparkleIcon';

import {
  Bar,
  DecisionDuration,
  ElapsedCell,
  isDiscovery,
  Pill,
  ProgressCell,
  ProgressLabel,
  Segment,
  SelectedAtDuration,
  subjectLabel,
  TimeCell,
  Timestamp,
} from './list/referralListCells';
import { INBOUND_REFERRALS_ROUTES, selectedGroupName, VALUE_NAMES } from './constants';
import {
  categorySegments,
  LIST_STATUS_DISPLAY,
  overallListStatus,
  progressSummary,
  referralDecidedAt,
} from './referralProgress';
import { sentToSummary } from './ReferralSentTo';
import SNFSlideout from './SNFSlideout';
import { TONE_STYLES } from './StatusBadge';

const columnHelper = createColumnHelper<Workflow>();

const TIME_TO_DECISION_INFO = 'From when the referral was created until a building first accepted or denied it.';
const TIME_SINCE_SELECTION_INFO = 'How long since the referral was marked Selected.';
const DATE_COMPLETED_INFO =
  "When the referral's outcome was recorded (Selected, Not Selected or Denied), or it was archived or canceled.";

function DecisionCell({ created, decidedAt }: { created: Date | null; decidedAt: Date | null }) {
  if (!created || !decidedAt) return <>—</>;
  return (
    <TimeCell>
      <DecisionDuration created={created} decidedAt={decidedAt} />
      <Timestamp>{simpleDateAtTime(created)}</Timestamp>
    </TimeCell>
  );
}

// The portal a run concerns. Every portal run carries source_system — it's the only value a
// sweep has, and retrieval runs carry it alongside their referral data — so this earns its own
// column rather than being crammed into the patient cell.
//
// The name comes from the caller (usePortalName) rather than a lookup here: portal names are
// the server's, and a column factory is not a component, so it can't hold a query of its own.
function portalLabel(workflow: Workflow, portalName: PortalName): string {
  return portalName(workflow.valueFor('source_system')?.value);
}

// The portal-name resolver the caller passes down — hooks/usePortalConnections' usePortalName.
type PortalName = (sourceSystem?: string | null) => string;

// Hoisted out of the column factory below so it keeps one stable identity across every call to
// inboundReferralsTableColumns — react-table's flexRender passes a `cell` def straight to
// React.createElement as the element's type, so a fresh closure (as this would be if it were
// defined inline, per every other cell here) makes React treat each recomputed columns array as
// a brand-new component type and remount the cell on every recompute. SNFCell has to survive
// that: it owns the slideout's open/closed state, and a remount silently snaps it shut.
function snfCell(info: CellContext<Workflow, unknown>) {
  return <SNFCell workflow={info.row.original} />;
}

// History's SNF cell also names the winner under a multi-SNF link (OLIO-3295); a second hoisted
// identity for the same reason as the first.
function snfHistoryCell(info: CellContext<Workflow, unknown>) {
  return <SNFCell workflow={info.row.original} showSelected />;
}

// Awaiting Admission's Time Since Selection (OLIO-3176). The clock itself lives on
// SelectedAtDuration in list/referralListCells.tsx, shared with the mobile card so the two cannot
// drift — the same split ElapsedCell/ElapsedDuration use. The column adds the timestamp beneath it,
// which the card has no room for.
function SelectedAtCell({ selectedAt }: { selectedAt: Date | null }) {
  if (!selectedAt) return <>—</>;
  return (
    <TimeCell>
      <SelectedAtDuration selectedAt={selectedAt} />
      <Timestamp>{simpleDateAtTime(selectedAt)}</Timestamp>
    </TimeCell>
  );
}

// Hoisted like snfCell: SelectedAtCell owns a clock, and a fresh closure per columns recompute would
// remount it (and reset its tick) on every render of the list.
function selectedAtCell(info: CellContext<Workflow, unknown>) {
  return <SelectedAtCell selectedAt={info.getValue() as Date | null} />;
}

// The winning SNF by name. One winner per row on this tab, so no "N SNFs" link and no slideout —
// that answers "which buildings was it sent to", which is History's question, not this tab's.
function selectedSnfCell(info: CellContext<Workflow, unknown>) {
  return <>{selectedGroupName(info.row.original) ?? '—'}</>;
}

// The three columns every tab shares, defined once so the tabs cannot drift apart on them.
const patientNameColumn = columnHelper.accessor((row) => subjectLabel(row), {
  id: 'patientName',
  header: 'Patient Name',
  cell: (info) => {
    const to = `${INBOUND_REFERRALS_ROUTES.BASE}/${info.row.original.id}`;
    // A sweep has no patient behind it, so it's muted/italic. Everything else — a named
    // referral or a not-yet-named one ("New referral" / "Portal referral") — is a real,
    // clickable referral row and should look like one (OLIO-3496).
    return isDiscovery(info.row.original) ? (
      <SweepLabel to={to} data-testid='workflow-type'>
        {info.getValue()}
      </SweepLabel>
    ) : (
      <PatientLink to={to}>{info.getValue()}</PatientLink>
    );
  },
  size: 200,
  enableSorting: false,
});

// The payer it RESOLVED to and nothing else, so the column agrees with the Payer filter beside it
// and Insights, which read the same resolved value (OLIO-3416). Deliberately empty until it
// resolves, rather than falling back to the payer the packet extracted — the same choice the
// Referral Source column makes below: an extracted fallback makes a referral that resolved to
// nothing look like it had a payer, and reads as a contradiction next to the filter's "None". The
// extracted payer is still on the referral page.
const payerColumn = columnHelper.accessor((row) => row.resolvedAttrValue(VALUE_NAMES.EXTERNAL_PAYER_NAME) ?? '', {
  id: 'payer',
  header: 'Primary Payer',
  // Not applicable to a sweep (no patient, no insurance) — blank rather than a dash, so the
  // row doesn't read as a referral whose payer is merely unknown.
  cell: (info) => (isDiscovery(info.row.original) ? '' : info.getValue() || '—'),
  size: 140,
  enableSorting: false,
});

// The name it RESOLVED to and nothing else, so the column agrees with the filter beside it
// (OLIO-3161): several wordings share one reporting name, and this column is what the filter
// matches on.
//
// Deliberately empty until it resolves, rather than falling back to the wording the referral
// captured. The fallback made a referral that has resolved to nothing look like it had a source,
// which is the opposite of what the row needs to say, and it read as a contradiction next to the
// filter's own "None" option: those rows came back under None with text in this column. The
// wording is still on the referral page, where it belongs. A referral bridged from an Olio referral has its
// sending building as its source, which needs no resolving. The filter doesn't match a building yet, so such a row
// still comes back under None; that half is deferred work ("Referral source as an Olio building: follow-up
// changes", Inbound Referrals list).
const referralSourceColumn = columnHelper.accessor(
  (row) => row.referralSourceGroup?.name ?? row.resolvedAttrValue(VALUE_NAMES.REFERRAL_SOURCE) ?? '',
  {
    id: 'referralSource',
    header: 'Referral Source',
    cell: (info) => (isDiscovery(info.row.original) ? '' : info.getValue() || '—'),
    size: 150,
    enableSorting: false,
  }
);

const sharedColumns = (portalName: PortalName, buildingLabel: string) => [
  patientNameColumn,
  columnHelper.accessor((row) => portalLabel(row, portalName), {
    id: 'portal',
    header: 'Portal',
    // Useful for both types: which portal this work concerns.
    cell: (info) => info.getValue() || '—',
    size: 110,
    enableSorting: false,
  }),
  payerColumn,
  referralSourceColumn,
  columnHelper.display({
    id: 'snf',
    header: buildingLabel,
    size: 140,
    cell: snfCell,
  }),
  columnHelper.display({
    id: 'progress',
    header: 'Progress',
    size: 190,
    cell: (info) => {
      const workflow = info.row.original;
      const { index, total, categoryName } = progressSummary(workflow);
      return (
        <ProgressCell>
          <Bar data-testid='progress-bar'>
            {categorySegments(workflow).map((segment) => (
              <Tooltip key={segment.key} text={`${segment.name}: ${LIST_STATUS_DISPLAY[segment.status].label}`}>
                <Segment
                  data-testid='progress-segment'
                  $color={TONE_STYLES[LIST_STATUS_DISPLAY[segment.status].tone].accent}
                />
              </Tooltip>
            ))}
          </Bar>
          <ProgressLabel>{total > 0 ? `${index} of ${total} – ${categoryName}` : '—'}</ProgressLabel>
        </ProgressCell>
      );
    },
  }),
  columnHelper.display({
    id: 'currentStatus',
    header: 'Status',
    size: 130,
    cell: (info) => {
      const status = overallListStatus(info.row.original);
      const display = LIST_STATUS_DISPLAY[status];
      return (
        <Pill $tone={display.tone} data-testid='list-status'>
          {status === 'processing' && <AISparkleIcon width={12} height={12} color={TONE_STYLES[display.tone].color} />}
          {display.label}
        </Pill>
      );
    },
  }),
  columnHelper.accessor((row) => row.createdAt, {
    id: 'timeElapsed',
    header: () => <InfoHeader label='Time to Decision' info={TIME_TO_DECISION_INFO} />,
    size: 160,
    enableSorting: false,
    cell: (info) => (
      <ElapsedCell created={info.getValue() as Date | null} decidedAt={referralDecidedAt(info.row.original)} />
    ),
  }),
];

// The Awaiting Admission tab's columns (OLIO-3176), listed outright rather than filtered out of the
// shared set: it keeps three of eight and adds two, so an allow-list would be longer than this and
// would quietly pick up any column added to the shared set later. Time Since Selection is the tab's
// default sort and its only sortable column (the api sorts on selected_at, unplaced rows last).
const awaitingColumns = () => [
  patientNameColumn,
  payerColumn,
  referralSourceColumn,
  columnHelper.display({
    id: 'selectedSnf',
    header: 'Selected SNF',
    size: 150,
    cell: selectedSnfCell,
  }),
  columnHelper.accessor((row) => row.selectedAt, {
    // The id doubles as the sort key the api receives (serializeSort), so it names the column.
    id: 'selectedAt',
    header: () => <InfoHeader label='Time Since Selection' info={TIME_SINCE_SELECTION_INFO} />,
    size: 160,
    enableSorting: true,
    cell: selectedAtCell,
  }),
];

// The History tab's own columns. Progress is gone — every row here is finished; Time to Decision
// becomes a fixed span to the decision; and Date Completed, the conclusion itself, closes the row
// and is the tab's default sort (the only sortable column in the table — the api sorts on
// concluded_at, undated rows last).
const historyColumns = () => [
  columnHelper.accessor((row) => row.createdAt, {
    id: 'timeSpent',
    header: () => <InfoHeader label='Time to Decision' info={TIME_TO_DECISION_INFO} />,
    size: 160,
    enableSorting: false,
    cell: (info) => (
      <DecisionCell created={info.getValue() as Date | null} decidedAt={referralDecidedAt(info.row.original)} />
    ),
  }),
  columnHelper.accessor((row) => row.concludedAt, {
    // The id doubles as the sort key the api receives (serializeSort), so it names the column.
    id: 'concludedAt',
    header: () => <InfoHeader label='Date Completed' info={DATE_COMPLETED_INFO} />,
    size: 140,
    enableSorting: true,
    cell: (info) => {
      const concluded = info.getValue() as Date | null;
      return concluded ? simpleDate(concluded) : '—';
    },
  }),
];

const inboundReferralsTableColumns = (
  portalName: PortalName,
  { tab = 'active', buildingLabel = 'SNF' }: { tab?: Tab; buildingLabel?: string } = {}
) => {
  if (tab === 'awaitingAdmission') return awaitingColumns();

  const shared = sharedColumns(portalName, buildingLabel);
  if (tab !== 'history') return shared;

  return shared
    .filter((column) => column.id !== 'progress' && column.id !== 'timeElapsed')
    .map((column) => (column.id === 'snf' ? { ...column, cell: snfHistoryCell } : column))
    .concat(historyColumns());
};

export default inboundReferralsTableColumns;

const PatientLink = styled(Link)`
  font-weight: 500;
  color: ${colors.blue100};
  text-decoration: none;
`;

// A sweep's identity cell. Single-line so a discovery row is the same height as a referral row,
// and muted/italic rather than link-blue because there's no patient behind it.
const SweepLabel = styled(Link)`
  font-weight: 500;
  font-style: italic;
  color: ${colors.black50};
  text-decoration: none;
`;

// `showSelected` (History, OLIO-3295): under a multi-SNF link, name the building that was selected,
// so the row answers "which one?" without the slideout. A single-SNF row IS its own answer.
function SNFCell({ workflow, showSelected = false }: { workflow: Workflow; showSelected?: boolean }) {
  const [open, setOpen] = useState(false);
  const groups = workflow.groups;

  if (isDiscovery(workflow)) return null;
  if (groups.length === 0) return <>{'—'}</>;
  if (groups.length === 1) return <>{groups[0].name}</>;

  const selected = showSelected ? selectedGroupName(workflow) : null;

  return (
    <>
      {/* A column, not siblings: the table cell lays its children out inline, and the sublabel
          belongs under the link, not beside it. */}
      <SNFStack>
        <SNFLink type='button' onClick={() => setOpen(true)}>
          {sentToSummary(groups)}
        </SNFLink>
        {selected && <SelectedLabel data-testid='snf-selected'>Selected: {selected}</SelectedLabel>}
      </SNFStack>
      {/* Always mounted so Foldout can animate on the false→true open transition. */}
      <SNFSlideout open={open} workflow={workflow} onClose={() => setOpen(false)} />
    </>
  );
}

const SNFStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const SelectedLabel = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 12px;
`;

const SNFLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: ${colors.blue100};
  text-decoration: underline;
  cursor: pointer;
`;
